import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  PlayArrow as PlayArrowIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

interface Recipient {
  id: number;
  email: string;
  name: string;
  active: boolean;
  added_by: string;
  created_at: string;
  updated_at: string;
}

interface SchedulerStatus {
  schedulerActive: boolean;
  scheduledTime: string;
  nextRun: string;
  nextRunFormatted: string;
}

const ReportRecipients: React.FC = () => {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState<{ open: boolean; recipient: Recipient | null }>({
    open: false,
    recipient: null
  });
  const [newRecipient, setNewRecipient] = useState({ email: '', name: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  const [sendingTest, setSendingTest] = useState<number | null>(null);
  const [triggeringDaily, setTriggeringDaily] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState<SchedulerStatus | null>(null);

  // Fetch recipients
  const fetchRecipients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/report-recipients', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success) {
        setRecipients(data.recipients);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load recipients',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch scheduler status
  const fetchSchedulerStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/report-recipients/scheduler-status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success) {
        setSchedulerStatus(data);
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  useEffect(() => {
    fetchRecipients();
    fetchSchedulerStatus();
    // Refresh scheduler status every minute
    const interval = setInterval(fetchSchedulerStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  // Add new recipient
  const handleAddRecipient = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/report-recipients', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: newRecipient.email,
          name: newRecipient.name || newRecipient.email.split('@')[0],
          added_by: 'admin'
        })
      });
      const data = await response.json();

      if (data.success) {
        setSnackbar({
          open: true,
          message: 'Recipient added successfully',
          severity: 'success'
        });
        setAddDialogOpen(false);
        setNewRecipient({ email: '', name: '' });
        fetchRecipients();
      } else {
        throw new Error(data.error);
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to add recipient',
        severity: 'error'
      });
    }
  };

  // Update recipient
  const handleUpdateRecipient = async () => {
    if (!editDialog.recipient) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/report-recipients/${editDialog.recipient.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: editDialog.recipient.email,
          name: editDialog.recipient.name,
          active: editDialog.recipient.active
        })
      });
      const data = await response.json();

      if (data.success) {
        setSnackbar({
          open: true,
          message: 'Recipient updated successfully',
          severity: 'success'
        });
        setEditDialog({ open: false, recipient: null });
        fetchRecipients();
      } else {
        throw new Error(data.error);
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update recipient',
        severity: 'error'
      });
    }
  };

  // Delete recipient
  const handleDeleteRecipient = async (id: number) => {
    if (!window.confirm('Are you sure you want to remove this recipient?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/report-recipients/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success) {
        setSnackbar({
          open: true,
          message: 'Recipient removed successfully',
          severity: 'success'
        });
        fetchRecipients();
      } else {
        throw new Error(data.error);
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to remove recipient',
        severity: 'error'
      });
    }
  };

  // Send test report
  const handleSendTest = async (id: number) => {
    setSendingTest(id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/report-recipients/test/${id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success) {
        setSnackbar({
          open: true,
          message: data.message,
          severity: 'success'
        });
      } else {
        throw new Error(data.error);
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send test report',
        severity: 'error'
      });
    } finally {
      setSendingTest(null);
    }
  };

  // Trigger daily report
  const handleTriggerDaily = async () => {
    if (!window.confirm('Send daily report to all active recipients now?')) return;

    setTriggeringDaily(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/report-recipients/trigger-daily', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success) {
        setSnackbar({
          open: true,
          message: data.message,
          severity: 'success'
        });
      } else {
        throw new Error(data.error);
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to trigger daily report',
        severity: 'error'
      });
    } finally {
      setTriggeringDaily(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Daily Report Recipients
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={triggeringDaily ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                onClick={handleTriggerDaily}
                disabled={triggeringDaily}
              >
                Send Now
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
              >
                Add Recipient
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Scheduler Status Card */}
        {schedulerStatus && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <ScheduleIcon color="primary" />
                  <Box flex={1}>
                    <Typography variant="h6">Automated Daily Reports</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Scheduled to run daily at {schedulerStatus.scheduledTime}
                    </Typography>
                    <Typography variant="body2" color="primary">
                      Next run: {schedulerStatus.nextRunFormatted}
                    </Typography>
                  </Box>
                  <Chip
                    label={schedulerStatus.schedulerActive ? 'Active' : 'Inactive'}
                    color={schedulerStatus.schedulerActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recipients Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <EmailIcon color="action" />
              <Typography variant="h6">
                Recipients ({recipients.length})
              </Typography>
              <Tooltip title="Recipients will receive the daily comprehensive sales report every morning at 7 AM EST">
                <InfoIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Added By</TableCell>
                    <TableCell>Added On</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipients.map((recipient) => (
                    <TableRow key={recipient.id}>
                      <TableCell>{recipient.email}</TableCell>
                      <TableCell>{recipient.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={recipient.active ? 'Active' : 'Inactive'}
                          color={recipient.active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{recipient.added_by}</TableCell>
                      <TableCell>
                        {format(parseISO(recipient.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Send test report">
                          <IconButton
                            size="small"
                            onClick={() => handleSendTest(recipient.id)}
                            disabled={sendingTest === recipient.id || !recipient.active}
                          >
                            {sendingTest === recipient.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SendIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => setEditDialog({ open: true, recipient })}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRecipient(recipient.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {recipients.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No recipients added yet. Click "Add Recipient" to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Add Recipient Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Recipient</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              value={newRecipient.email}
              onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
              required
            />
            <TextField
              label="Name"
              fullWidth
              value={newRecipient.name}
              onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
              placeholder="Optional - defaults to email username"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false);
            setNewRecipient({ email: '', name: '' });
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddRecipient}
            variant="contained"
            disabled={!newRecipient.email || !newRecipient.email.includes('@')}
          >
            Add Recipient
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Recipient Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, recipient: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Recipient</DialogTitle>
        <DialogContent>
          {editDialog.recipient && (
            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              <TextField
                label="Email Address"
                type="email"
                fullWidth
                value={editDialog.recipient.email}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  recipient: { ...editDialog.recipient!, email: e.target.value }
                })}
                required
              />
              <TextField
                label="Name"
                fullWidth
                value={editDialog.recipient.name}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  recipient: { ...editDialog.recipient!, name: e.target.value }
                })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editDialog.recipient.active}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      recipient: { ...editDialog.recipient!, active: e.target.checked }
                    })}
                  />
                }
                label="Active"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, recipient: null })}>
            Cancel
          </Button>
          <Button onClick={handleUpdateRecipient} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReportRecipients;